import { useEffect, useState, useCallback, useMemo } from "react";
import { fetchChurchInvitationsAsync, fetchChurchInfoAsync, fetchDistrictInvitationsAsync, fetchDistrictInfoAsync, fetchNationInvitationsAsync, fetchNationInfoAsync } from "@features/invitations/services/invitationSvc";
import { Invitation } from "@features/invitations/types/invitation";
import { InvitationListQueryParam } from "@features/invitations/types/invitationListQueryParam";
import { InvitationListResult } from "@features/invitations/types/invitation";
import { EntityQueryParam } from "@features/invitations/types/entityQueryParam";
import { getApiScopeFromValue } from "@features/invitations/utils/getScopeFromParam";



/**
 * Custom React hook to fetch and manage a paginated list of invitations based on the provided query parameters and scope.
 *
 * Depending on the `scope` parameter, it fetches pending invitations for nation, district, or church levels.
 * The hook maintains pagination state, loading state, and provides a `reload` function to manually refresh the data.
 *
 * @param params - The query parameters for fetching the invitation list, including scope, page, status, and pageSize.
 * @returns An object containing invitation data, pagination information, loading state, and a reload function.
 */
export function useInvitations(params: InvitationListQueryParam): InvitationListResult {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<Invitation[]>([]);
    const [first_page_url, setFirstPageUrl] = useState<string | undefined>(undefined);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [last_page_url, setLastPageUrl] = useState<string | undefined>(undefined);
    const [links, setLinks] = useState<{ url: string | null; label: string; active: boolean }[] | undefined>(undefined);
    const [next_page_url, setNextPageUrl] = useState<string | null | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [prev_page_url, setPrevPageUrl] = useState<string | null | undefined>(undefined);
    const [to, setTo] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const requestParams = useMemo(
        () => ({
            scope: params.scope,
            scope_ref_id: params.scope_ref_id,
            page: params.page,
            status: params.status,
            all: params.all,
            pageSize: params.pageSize,
            query: params.query,
            freetext: params.freetext,
            email: params.email,
            role: params.role,
            trn: params.trn,
            departmentName: params.departmentName,
            departmentId: params.departmentId,
        }),
        [
            params.scope,
            params.scope_ref_id,
            params.page,
            params.status,
            params.all,
            params.pageSize,
            params.query,
            params.freetext,
            params.email,
            params.role,
            params.trn,
            params.departmentName,
            params.departmentId,
        ]
    );

    const reload = useCallback(async () => {
        setLoading(true);

        try {
            let fetchFn;
            const apiScope = getApiScopeFromValue(requestParams.scope ?? "");

            if (!requestParams.scope) {
                setData([]);
                setTotal(0);
                return;
                // throw new Error("Invalid scope"); //TODO: Change documentation for how errors are handled
            }
            switch (apiScope) {
                case "nation":
                    fetchFn = fetchNationInvitationsAsync;
                    break;
                case "district":
                    fetchFn = fetchDistrictInvitationsAsync;
                    break;
                case "church":
                    fetchFn = fetchChurchInvitationsAsync;
                    break;
                default:
                    setData([]);
                    setTotal(0);
                    return;
                // throw new Error("Invalid scope"); //TODO: Change documentation for how errors are handled
            }

            const response = await fetchFn(requestParams);

            // If the response is successful, update state variables with the fetched data
            if (response.isSuccess()) {
                setCurrentPage(response.getData()?.current_page || 1);
                setData(response.getData()?.data || []);
                setFirstPageUrl(response.getData()?.first_page_url);
                setFrom(response.getData()?.from);
                setLastPage(response.getData()?.last_page);
                setLastPageUrl(response.getData()?.last_page_url);
                setLinks(response.getData()?.links);
                setNextPageUrl(response.getData()?.next_page_url);
                setPath(response.getData()?.path);
                setPerPage(response.getData()?.per_page);
                setPrevPageUrl(response.getData()?.prev_page_url);
                setTo(response.getData()?.to);
                setTotal(response.getData()?.total || 0);
            } else {
                setData([]);
                setTotal(0);
            }
        } finally {
            setLoading(false);
        }
    }, [requestParams]);

    useEffect(() => {
        void reload();
    }, [reload]);

    return { current_page, data, first_page_url, from, last_page, last_page_url, links, next_page_url, path, per_page, prev_page_url, to, total, loading, reload };
}


/**
 * Fetches the name of a scope (nation, district, or church) based on the provided parameters.
 *
 * The function determines which fetch function to use depending on the `scope` value in `params`,
 * constructs the appropriate parameter object, and retrieves the corresponding scope name from the response.
 *
 * @param params - The query parameters containing the scope type and reference ID.
 *   - `scope`: A string representing the scope type ('n' for nation, 'd' for district, 'c' for church).
 *   - `scope_ref_id`: The reference ID for the scope.
 * @returns A promise that resolves to an object containing the `scopeName`, or `undefined` if not found or on error.
 */
export async function fetchScopeName(params: EntityQueryParam): Promise<{ scopeName: string } | undefined> {
    try {
        let fetchFn;
        let newParam;
        let scopeName = "Unknown";
        const apiScope = getApiScopeFromValue(params.scope ?? "");

        // Determine which fetch function to use based on the scope
        switch (apiScope) {
            case "nation":
                // Use the nation fetch function
                fetchFn = fetchNationInfoAsync;

                // Construct the parameter object for the nation scope
                newParam = { nationId: params.scope_ref_id };
                break;
            case "district":
                // Use the district fetch function
                fetchFn = fetchDistrictInfoAsync;

                // Construct the parameter object for the district scope
                newParam = { districtId: params.scope_ref_id };
                break;
            case "church":
                // Use the church fetch function
                fetchFn = fetchChurchInfoAsync;

                // Construct the parameter object for the church scope
                newParam = { churchId: params.scope_ref_id };
                break;
            default:
                return undefined;
        }

        const response = await fetchFn(newParam);

        // If no response, return undefined
        if (!response) return undefined;

        const data = response.getData()?.data?.[0];

        // Extract the scope name based on the scope type
        if (apiScope === "nation") {
            scopeName = data?.nation_name ?? "Unknown";
        } else if (apiScope === "district") {
            scopeName = data?.district_name ?? "Unknown";
        } else if (apiScope === "church") {
            scopeName = data?.church_name ?? "Unknown";
        }

        return { scopeName };
    } catch (error) {
        // Optionally log error
        return undefined;
    }
}
